import { MouseEvent, useMemo, useState } from "react";
import {
  Avatar,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
} from "@mui/material";
import { useUser } from "../../../hooks/useUser";
import { useNavigate } from "react-router-dom";
import { usePermissionService } from "../../../services/permissionService";
import useLogout from "../../../hooks/useLogout";
import InstallDesktopIcon from "@mui/icons-material/InstallDesktop";
import PersonIcon from "@mui/icons-material/Person";
import SchoolIcon from "@mui/icons-material/School";
import BookIcon from "@mui/icons-material/Book";
import NewReleasesIcon from "@mui/icons-material/NewReleases";
import EmojiEventsIcon from "@mui/icons-material/EmojiEvents";
import HistoryIcon from "@mui/icons-material/History";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import WorkspacePremiumIcon from "@mui/icons-material/WorkspacePremium";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import SettingsIcon from "@mui/icons-material/Settings";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import LogoutIcon from "@mui/icons-material/Logout";
import * as Sentry from "@sentry/react";
import {
  useAccountSwitchList,
  accountSwitchSelectAccountCreate,
} from "../../../api-client/account-switch/account-switch";
import { enqueueSnackbar } from "notistack";
import { queryClient } from "../../../lib/queryClient.ts";

declare global {
  interface BeforeInstallPromptEvent extends Event {
    readonly platforms?: string[];
    prompt: () => Promise<void>;
    userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
  }
  interface Window {
    deferredPrompt: BeforeInstallPromptEvent | null | undefined;
  }
}

export default function UserMenu() {
  const logout = useLogout();
  const navigate = useNavigate();
  const user = useUser();
  const permissionService = usePermissionService();

  const { data: accounts } = useAccountSwitchList({
    query: {
      staleTime: Infinity, // Never goes stale
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      refetchOnMount: true,
    },
  });

  // Sort accounts alphabetically by name
  const sortedAccounts = useMemo(() => {
    if (!accounts) return [];
    return [...accounts].sort((a, b) => a.name.localeCompare(b.name));
  }, [accounts]);

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [accountId, setAccountId] = useState<string>(() => user.account?.id.toString() || "");
  const [isSwitching, setIsSwitching] = useState(false);
  const [showInstall, setShowInstall] = useState(() => !!window?.deferredPrompt);
  const open = Boolean(anchorEl);

  // Memoize admin check for performance
  const isAdmin = useMemo(
    () => (user ? permissionService.isAdmin(user).success : false),
    [permissionService, user]
  );

  const [prevAccountId, setPrevAccountId] = useState(user.account?.id);
  if (user.account?.id !== prevAccountId) {
    setPrevAccountId(user.account?.id);
    setAccountId(user.account?.id.toString() || "");
  }

  const handleMenuOpen = (event: MouseEvent<HTMLElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => setAnchorEl(null);

  const handleNavigate = (path: string) => {
    handleMenuClose();
    navigate(path);
  };

  const handleAccountSwitch = async (event: SelectChangeEvent) => {
    const newAccountId = event.target.value;
    setAccountId(newAccountId);
    setIsSwitching(true);

    try {
      await accountSwitchSelectAccountCreate(parseInt(newAccountId));
      await queryClient.invalidateQueries();
      setIsSwitching(false);
      handleMenuClose();
    } catch (error) {
      console.error("Failed to switch account:", error);
      enqueueSnackbar("Failed to switch account. Please try again.", {
        variant: "error",
      });
      // Revert to previous account ID on error
      setAccountId(user.account?.id.toString() || "");
      setIsSwitching(false);
    }
  };

  const handleInstallClick = async () => {
    if (!window?.deferredPrompt) return;

    window.deferredPrompt.prompt();

    const { outcome } = await window.deferredPrompt.userChoice;

    if (outcome === "accepted") {
      Sentry.captureMessage("User accepted the install prompt.", {
        level: "info",
      });
      setShowInstall(false);
    } else if (outcome === "dismissed") {
      Sentry.captureMessage("User dismissed the install prompt", {
        level: "info",
      });
    }

    window.deferredPrompt = null;
    handleMenuClose();
  };
  return (
    <>
      <Tooltip title="Account settings" open={open ? false : undefined}>
        <IconButton
          onClick={handleMenuOpen}
          size="small"
          sx={{ ml: 2 }}
          aria-label="Account settings"
        >
          <Avatar
            sx={{ width: 32, height: 32 }}
            src={user.imageUrl}
            alt={user.fullName}
          />
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleMenuClose}
        aria-label="User account menu"
        sx={{
          zIndex: 10001, // Higher than backdrop and loading screen
        }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              overflow: "visible",
              filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.32))",
              mt: 1.5,
              "& .MuiAvatar-root": { width: 32, height: 32, ml: -0.5, mr: 1 },
              // Make the menu scrollable when too tall while keeping the arrow visible
              "& .MuiMenu-list": {
                maxHeight: "80vh",
                overflowY: "auto",
              },
              "&:before": {
                content: '""',
                display: "block",
                position: "absolute",
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: "background.paper",
                transform: "translateY(-50%) rotate(45deg)",
                zIndex: 0,
              },
            },
          },
        }}
      >
        {sortedAccounts &&
          sortedAccounts?.length > 1 && [
            <FormControl key="class-switcher" sx={{ m: 1, minWidth: 200 }}>
              <InputLabel>Current Account</InputLabel>
              <Select
                label="Current Account"
                value={accountId}
                onChange={handleAccountSwitch}
                disabled={isSwitching}
                aria-label="Switch between accounts"
                endAdornment={
                  isSwitching && (
                    <CircularProgress
                      size={20}
                      sx={{ position: "absolute", right: 30 }}
                    />
                  )
                }
                MenuProps={{
                  sx: {
                    zIndex: 10002, // Higher than the user menu (10001)
                  },
                }}
              >
                {sortedAccounts.map((account) => (
                  <MenuItem key={account.id} value={account.id}>
                    {account.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>,
            <Divider key="class-switcher-divider" />,
          ]}

        {showInstall && (
          <MenuItem
            onClick={handleInstallClick}
            aria-label="Install app on device"
          >
            <ListItemIcon>
              <InstallDesktopIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Save to Home Screen</ListItemText>
          </MenuItem>
        )}

        <MenuItem
          onClick={() => handleNavigate("/app/profile/0")}
          aria-label="View your profile"
        >
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Profile</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/guide")}
          aria-label="View user guide"
        >
          <ListItemIcon>
            <BookIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>User Guide</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/updates")}
          aria-label="View what's new"
        >
          <ListItemIcon>
            <NewReleasesIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>What's New</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/achievements")}
          aria-label="View achievements"
        >
          <ListItemIcon>
            <EmojiEventsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Achievements</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/standings")}
          aria-label="View standings"
        >
          <ListItemIcon>
            <LeaderboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Standings</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/history")}
          aria-label="View game history"
        >
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Game History</ListItemText>
        </MenuItem>
        <MenuItem
          onClick={() => handleNavigate("/app/suggestions")}
          aria-label="View feature suggestions"
        >
          <ListItemIcon>
            <LightbulbIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Suggestions</ListItemText>
        </MenuItem>

        {isAdmin && [
          <Divider key="admin-divider" />,
          <MenuItem
            key="setupguide"
            onClick={() => handleNavigate("/app/getting_started")}
            aria-label="Getting Started guide"
          >
            <ListItemIcon>
              <SchoolIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Getting Started</ListItemText>
          </MenuItem>,
          <MenuItem
            key="badges"
            onClick={() => handleNavigate("/app/badges")}
            aria-label="Manage commendation"
          >
            <ListItemIcon>
              <WorkspacePremiumIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Commendation Management</ListItemText>
          </MenuItem>,
          <MenuItem
            key="controls"
            onClick={() => handleNavigate("/app/controls")}
            aria-label="World controls"
          >
            <ListItemIcon>
              <SettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>World Controls</ListItemText>
          </MenuItem>,
          <MenuItem
            key="admin"
            onClick={() => handleNavigate("/admin/")}
            aria-label="Admin panel"
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Admin</ListItemText>
          </MenuItem>,
        ]}

        <Divider />
        <MenuItem
          onClick={() => {
            handleMenuClose();
            logout();
          }}
          aria-label="Logout"
        >
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>
    </>
  );
}
